/**
 * 费用申请（事前，延伸需求 3.1）
 *
 * 申请人提出预期支出，批准后才能报销。
 *
 * 【与报销的区别】申请是**意向**，不占用预算的"已用"（它进"在途"），
 * 也不涉及发票。真正的钱在报销那一步认下——所以预算的硬控制点在报销提交时。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  ExpenseApplyStatus,
  EXPENSE_APPLY_STATUS_LABELS,
  ExpenseType,
  EXPENSE_TYPE_LABELS,
  FlowAction,
  PermissionCode,
} from '@ilc/shared';
import type { ExpenseApply } from '@ilc/shared';
import { expenseApi, flowApi, projectApi, contractApi } from '@/api/endpoints';
import { usePagedList } from '@/hooks/usePagedList';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { Money, moneyCellStyle } from '@/components/Money';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';

/** 与 shared 的 ExpenseApplyListQuery 对齐，避免各页面做类型断言 */
interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  expenseType?: ExpenseType;
  status?: ExpenseApplyStatus;
  mine?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  APPROVING: 'processing',
  APPROVED: 'success',
  REJECTED: 'warning',
  CANCELLED: 'default',
  CLOSED: 'default',
};

export function ExpenseApplyPage(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const navigate = useNavigate();
  const canApply = can(PermissionCode.EXPENSE_APPLY);
  const auth = useAuthStore((s) => s.auth);

  /**
   * 「谁能对这张单做什么」在客户端只有**一个用途：决定显不显示按钮**。
   * 真正的授权在服务端（按 id 校验归属；审批按任务的处理角色校验）。
   * 两处判据必须一致，否则会出现"按钮给了、点了 403"或者"能批却看不到按钮"。
   */
  const myRoles = useMemo(() => new Set((auth?.roles ?? []).map((r) => r.code)), [auth]);
  const isAdmin = myRoles.has('TENANT_ADMIN');
  /** 申请人动作（提交 / 撤销 / 删除）＝本人或租户管理员 */
  const isMine = (r: ExpenseApply): boolean => r.applicantId === auth?.userId || isAdmin;
  /** 审批动作＝这张单的待办处理角色命中我的角色（管理员例外，与后端一致） */
  const canApprove = (r: ExpenseApply): boolean =>
    Boolean(r.pendingTaskId) &&
    (isAdmin || (r.pendingRoleCode !== null && r.pendingRoleCode !== undefined && myRoles.has(r.pendingRoleCode)));

  const list = usePagedList<ExpenseApply, Query>({
    loader: (q) => expenseApi.listApplies(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitTarget, setSubmitTarget] = useState<ExpenseApply | null>(null);
  const [approveTarget, setApproveTarget] = useState<ExpenseApply | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ExpenseApply | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ExpenseApply | null>(null);
  const [budgetWarning, setBudgetWarning] = useState<string | null>(null);
  const [form] = Form.useForm();

  /* 详情抽屉：点「查看」时才拉，列表不背这个请求 */
  const [detail, setDetail] = useState<ExpenseApply | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const openDetail = async (row: ExpenseApply): Promise<void> => {
    setDetail({ ...row, approvals: [], currentNode: null } as ExpenseApply);
    setDetailLoading(true);
    try {
      setDetail(await expenseApi.applyDetail(row.id));
    } catch (err) {
      feedback.error(err instanceof Error ? err.message : '加载申请详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  /** 工程与合同下拉：可选字段，取不到就不挡路（`useResource` 会给出降级态） */
  const projects = useResource(() => projectApi.list(), { deps: [] });
  /* 合同下拉走**候选接口**（只回 id/合同号/名称，按数据范围过滤）。
   * 调列表接口要 contract:read，而普通员工与部门负责人没有这个码——
   * 页面一打开就会弹「没有执行该操作的权限（需要：contract:read）」。 */
  const contracts = useResource(() => contractApi.options(), { deps: [] });

  const formInitialValues = useMemo(() => ({ expenseType: ExpenseType.TRAVEL }), []);

  const columns = [
    {
      title: '申请单号',
      dataIndex: 'applyNo',
      width: 170,
      render: (v: string) => <span className="ilc-mono">{v}</span>,
    },
    { title: '申请人', dataIndex: 'applicantName', width: 100, render: (v: string | null) => v ?? '-' },
    { title: '部门', dataIndex: 'deptName', width: 110, render: (v: string | null) => v ?? '-' },
    {
      title: '费用类型',
      dataIndex: 'expenseType',
      width: 100,
      render: (v: string) => EXPENSE_TYPE_LABELS[v as ExpenseType] ?? v,
    },
    {
      title: '申请金额',
      dataIndex: 'amountCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} strong />,
    },
    {
      title: '已报销',
      dataIndex: 'reimbursedCents',
      width: 120,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} />,
    },
    {
      title: '可报销余额',
      dataIndex: 'availableCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      // 余额为 0 说明这张申请已经报满了，再报会被提示
      render: (v: number, r: ExpenseApply) => (
        <span style={{ color: v === 0 && r.amountCents > 0 ? '#cf1322' : undefined }}>
          <Money cents={v} />
        </span>
      ),
    },
    { title: '费用事由', dataIndex: 'reason', ellipsis: true },
    {
      title: '关联',
      key: 'links',
      width: 160,
      render: (_: unknown, r: ExpenseApply) => (
        <Space size={2} wrap>
          {r.projectName && <Tag>{r.projectName}</Tag>}
          {r.contractNo && <Tag color="blue">{r.contractNo}</Tag>}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 96,
      render: (v: string) => (
        <Tag color={STATUS_COLORS[v]}>{EXPENSE_APPLY_STATUS_LABELS[v as ExpenseApplyStatus] ?? v}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 190,
      fixed: 'right' as const,
      render: (_: unknown, r: ExpenseApply) => (
        <Space size={4}>
          {/* 先给一个「查看」——**所有状态都能点**。从前审批中的单在列表里
              一个操作都没有，点不进去也看不到它走到哪一步（2026-09-24 用户报的
              "流程中找不到这个单据"有一半是这个）。 */}
          <Button size="small" type="link" onClick={() => void openDetail(r)}>
            查看
          </Button>
          {/* 待办在我这儿才出现——判据与后端授权同一条：处理角色命中我的角色 */}
          {canApprove(r) && (
            <>
              <Button size="small" type="link" onClick={() => setApproveTarget(r)}>
                审批
              </Button>
              <Button size="small" type="link" danger onClick={() => setRejectTarget(r)}>
                退回
              </Button>
            </>
          )}
          {/* 申请人动作：只有**本人**（或租户管理员）才看得到。
              从前这条判断只看了权限码，于是部门负责人在下属的申请上也能看到
              "撤销"——那是别人的单子，他不该有这个按钮。 */}
          {canApply && isMine(r) && (r.status === ExpenseApplyStatus.DRAFT || r.status === ExpenseApplyStatus.REJECTED) && (
            <Button size="small" type="link" onClick={() => setSubmitTarget(r)}>
              提交审批
            </Button>
          )}
          {/* 【审批中也能撤销】两个理由：
           *   · 真实业务里"我不想申请了"很常见，而且单据已经提交、改不了内容；
           *   · 更要紧的是**卡住的单据**：状态是审批中、但流程没起来（老代码
           *     起流程失败会留下这种死单据）。若这里不给撤销，它就既审不了、
           *     也撤不掉，界面上完全无路可走。
           * 服务端撤销时会**同时终止在办流程**，审批人那边的待办一并撤掉。 */}
          {canApply &&
            isMine(r) &&
            (r.status === ExpenseApplyStatus.DRAFT ||
              r.status === ExpenseApplyStatus.REJECTED ||
              r.status === ExpenseApplyStatus.APPROVING) && (
              <Button size="small" type="link" danger onClick={() => setCancelTarget(r)}>
                撤销
              </Button>
            )}
          {r.status === ExpenseApplyStatus.APPROVED && (
            <Button
              size="small"
              type="link"
              onClick={() => navigate(`/business/expenses/reimburse?applyId=${r.id}`)}
            >
              去报销
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const save = async (): Promise<void> => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          expenseApi.createApply({
            expenseType: v.expenseType,
            amountCents: Math.round(Number(v.amountYuan ?? 0) * 100),
            reason: v.reason,
            projectId: v.projectId ?? null,
            contractId: v.contractId ?? null,
            budgetId: null,
          }),
        successMessage: '费用申请已保存',
        offline: () => undefined,
        offlineMessage: '后端费用接口尚未就绪，本次保存仅在本地演示',
      });
      if (!result.ok) return;
      setCreating(false);
      form.resetFields();
      /**
       * 预算提示要**单独展示**而不是塞进 toast。
       * 它可能是一句"这笔会让预算执行率达到 92%"——用户需要看清数字再决定要不要提交，
       * 而 toast 几秒就消失了。
       */
      const warning = (result.data as { budgetWarning?: string | null } | null)?.budgetWarning ?? null;
      setBudgetWarning(warning);
      list.reload();
    } finally {
      setSaving(false);
    }
  };

  const doSubmit = async (note: string): Promise<void> => {
    if (!submitTarget) return;
    const result = await performAction({
      action: () => expenseApi.submitApply(submitTarget.id),
      successMessage: `申请 ${submitTarget.applyNo} 已提交审批`,
      offline: () => undefined,
      offlineMessage: '后端接口尚未就绪，本次提交仅在本地演示',
    });
    if (result.ok) {
      if (note) feedback.info(`提交说明：${note}`);
      setSubmitTarget(null);
      list.reload();
    }
  };

  /**
   * 待办处理（通过 / 退回）。
   *
   * 【为什么列表里要能直接审批】用户的原话是"能看到费用申请单了，但却没有审批按钮"——
   * 部门负责人打开的就是这一页，让他为了批一张单再跳到「待办与审批」是多余的一跳。
   * 任务 id 由列表接口带出（`pendingTaskId`），授权仍由服务端按处理角色判定。
   */
  const doHandleTask = async (
    row: ExpenseApply | null,
    action: FlowAction,
    comment: string,
  ): Promise<void> => {
    if (!row?.pendingTaskId) return;
    const result = await performAction({
      action: () =>
        flowApi.handle(row.pendingTaskId as string, {
          action,
          comment: comment || undefined,
        }),
      successMessage:
        action === FlowAction.APPROVE
          ? `${row.applyNo} 已通过，流程进入下一步`
          : `${row.applyNo} 已退回经办人`,
    });
    if (result.ok) {
      setApproveTarget(null);
      setRejectTarget(null);
      list.reload();
    }
  };

  const doCancel = async (reason: string): Promise<void> => {
    if (!cancelTarget) return;
    const result = await performAction({
      action: () => expenseApi.cancelApply(cancelTarget.id, reason),
      successMessage: '申请已撤销',
      offline: () => undefined,
      offlineMessage: '后端接口尚未就绪，本次撤销仅在本地演示',
    });
    if (result.ok) {
      setCancelTarget(null);
      list.reload();
    }
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="费用申请"
        description="事前申请：先说清要花什么钱，批准后再发生费用"
        extra={
          <Space>
            {canApply && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
                新建申请
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
          </Space>
        }
      />

      {budgetWarning && (
        <Alert
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message="预算提示"
          description={budgetWarning}
          onClose={() => setBudgetWarning(null)}
        />
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="申请阶段只提示预算，不拦截；真正的预算控制点在报销提交时"
        description="申请是意向，钱在报销那一步才认下。所以申请时即使超预算也允许提交，由审批人判断。"
      />

      <DataTable<ExpenseApply>
        storageKey="expense-applies"
        exportFileName="费用申请"
        exportColumns={[
          { title: '申请单号', dataIndex: 'applyNo' },
          { title: '申请人', dataIndex: 'applicantName' },
          { title: '部门', dataIndex: 'deptName' },
          { title: '费用类型', value: (r) => EXPENSE_TYPE_LABELS[r.expenseType] ?? r.expenseType },
          { title: '申请金额(元)', value: (r) => (r.amountCents / 100).toFixed(2) },
          { title: '已报销(元)', value: (r) => (r.reimbursedCents / 100).toFixed(2) },
          { title: '可报销余额(元)', value: (r) => (r.availableCents / 100).toFixed(2) },
          { title: '费用事由', dataIndex: 'reason' },
          { title: '状态', value: (r) => EXPENSE_APPLY_STATUS_LABELS[r.status] ?? r.status },
        ]}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        scrollX={1400}
        emptyText="还没有费用申请。点右上角「新建申请」开始。"
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="单号 / 事由"
              style={{ width: 220 }}
              onSearch={(v) => list.setQuery({ keyword: v || undefined })}
            />
            <Select
              allowClear
              placeholder="费用类型"
              style={{ width: 130 }}
              value={list.query.expenseType}
              onChange={(v) => list.setQuery({ expenseType: v })}
              options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="状态"
              style={{ width: 120 }}
              value={list.query.status}
              onChange={(v) => list.setQuery({ status: v })}
              options={Object.entries(EXPENSE_APPLY_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Button
              type={list.query.mine === true ? 'primary' : 'default'}
              onClick={() => list.setQuery({ mine: list.query.mine === true ? undefined : true })}
            >
              只看我的
            </Button>
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Modal
        open={creating}
        title="新建费用申请"
        width={680}
        onCancel={() => setCreating(false)}
        onOk={() => void save()}
        confirmLoading={saving}
        destroyOnHidden
        okText="保存"
      >
        <Form key="create" form={form} layout="vertical" preserve={false} initialValues={formInitialValues}>
          <Row gutter={12}>
            <Col xs={24} md={10}>
              <Form.Item name="expenseType" label="费用类型" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={14}>
              <Form.Item name="amountYuan" label="申请金额（元）" rules={[{ required: true, message: '请填写金额' }]}>
                <InputNumber style={{ width: '100%' }} precision={2} min={0.01} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="reason" label="费用事由" rules={[{ required: true, message: '请填写事由' }]}>
            <Input.TextArea rows={3} maxLength={1000} placeholder="如：赴泰安项目部对接施工进度，预计 3 天" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="projectId" label="关联工程" extra="可选，用于按工程归集成本">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={projects.loading ? '加载中…' : '可选'}
                  loading={projects.loading}
                  notFoundContent={projects.loading ? '加载中…' : '工程接口未就绪或暂无工程，可留空'}
                  options={(projects.data ?? []).map((p: { id: string; name: string }) => ({ value: p.id, label: p.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="contractId" label="关联合同" extra="可选">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={contracts.loading ? '加载中…' : '可选'}
                  loading={contracts.loading}
                  notFoundContent={contracts.loading ? '加载中…' : '合同接口未就绪或暂无合同，可留空'}
                  options={(contracts.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.contractNo} ${c.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <ConfirmDanger
        open={approveTarget !== null}
        title={`审批通过：${approveTarget?.applyNo ?? ''}（${approveTarget?.pendingNodeName ?? ''}）`}
        description="通过后流程进入下一节点。意见会写进审批留痕备查。"
        level="normal"
        reasonLabel="审批意见（选填）"
        minReasonLength={0}
        confirmText="确认通过"
        onConfirm={(note) => void doHandleTask(approveTarget, FlowAction.APPROVE, note)}
        onCancel={() => setApproveTarget(null)}
      />
      <ConfirmDanger
        open={rejectTarget !== null}
        title={`退回经办人：${rejectTarget?.applyNo ?? ''}（${rejectTarget?.pendingNodeName ?? ''}）`}
        description="退回后单据回到经办人手上，他修改后可重新提交。退回原因会展示给他。"
        level="high"
        reasonLabel="退回原因（必填）"
        minReasonLength={4}
        confirmText="确认退回"
        onConfirm={(reason) => void doHandleTask(rejectTarget, FlowAction.REJECT_TO_HANDLER, reason)}
        onCancel={() => setRejectTarget(null)}
      />
      <ConfirmDanger
        open={submitTarget !== null}
        title={`提交审批：${submitTarget?.applyNo ?? ''}`}
        description="提交后进入审批链（部门负责人 → 财务初审 → 领导，按金额阈值分流）。审批期间不能修改。"
        level="normal"
        reasonLabel="提交说明（选填）"
        minReasonLength={0}
        confirmText="确认提交"
        onConfirm={(note) => void doSubmit(note)}
        onCancel={() => setSubmitTarget(null)}
      />

      <ConfirmDanger
        open={cancelTarget !== null}
        title={`撤销申请：${cancelTarget?.applyNo ?? ''}`}
        description={
          cancelTarget?.status === ExpenseApplyStatus.APPROVING ? (
            <span>
              这张单正在审批中。撤销会<strong>同时终止流程</strong>——审批人那边的待办会一并撤掉，
              单据置为「已撤销」且不能恢复。
            </span>
          ) : (
            '撤销后该申请不再可用，也不能恢复。已批准的申请不能撤销（要走报销单那边的处理）。'
          )
        }
        level="high"
        reasonLabel="撤销原因（必填，将写入操作日志）"
        minReasonLength={4}
        confirmText="确认撤销"
        onConfirm={(reason) => void doCancel(reason)}
        onCancel={() => setCancelTarget(null)}
      />

      {/* —— 申请详情：走到哪一步、等谁、谁审过 ——
          费用申请从前只有一个列表接口，"提交之后它在哪、卡在谁那里"没有任何地方能看。
          审批留痕读的是流程引擎的任务（不另存一份），与报销详情同一口径。 */}
      <Drawer
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={560}
        title={detail ? `费用申请 ${detail.applyNo}` : '费用申请'}
        destroyOnHidden
      >
        {detail ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {detail.flowMissing ? (
              <Alert
                type="error"
                showIcon
                message="这张单没有流程记录"
                description="状态是「审批中」，但流程引擎里查不到它的审批记录——通常是提交时流程没能启动（该问题已修复：现在起流程失败会把状态退回草稿，可以重新提交）。这一张请让管理员在「待办与审批」里确认，或删除后重新提交。"
              />
            ) : null}

            {detail.currentNode ? (
              <Alert
                type={detail.currentNode.memberCount > 0 ? 'info' : 'warning'}
                showIcon
                message={`当前节点：${detail.currentNode.nodeName}`}
                description={
                  detail.currentNode.memberCount > 0 ? (
                    <span>
                      处理角色「{detail.currentNode.roleName}」，当前有 {detail.currentNode.memberCount} 名在职成员。
                    </span>
                  ) : (
                    <span>
                      「{detail.currentNode.roleName}」这个角色当前<strong>一个成员都没有</strong>
                      ，这张单暂时没有人能审。请让管理员在「用户管理」里把该角色分配给相关同事；
                      管理员本人也可以直接在「待办与审批」里处理。
                    </span>
                  )
                }
              />
            ) : null}

            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="申请人">
                {detail.applicantName ?? '-'}（{detail.deptName ?? '未设部门'}）
              </Descriptions.Item>
              <Descriptions.Item label="费用类型">
                <Tag>{EXPENSE_TYPE_LABELS[detail.expenseType] ?? detail.expenseType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="申请金额">
                <Money cents={detail.amountCents} strong />
              </Descriptions.Item>
              <Descriptions.Item label="已报销 / 可报销余额">
                <Money cents={detail.reimbursedCents} /> / <Money cents={detail.availableCents} />
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_COLORS[detail.status]}>{EXPENSE_APPLY_STATUS_LABELS[detail.status] ?? detail.status}</Tag>
                {detail.rejectCount > 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    被退回 {detail.rejectCount} 次
                  </Typography.Text>
                ) : null}
              </Descriptions.Item>
              <Descriptions.Item label="费用事由">{detail.reason}</Descriptions.Item>
              <Descriptions.Item label="关联项目 / 合同">
                {[detail.projectName, detail.contractNo].filter(Boolean).join(' / ') || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="提交 / 批准时间">
                {[
                  detail.submittedAt ? dayjs(detail.submittedAt).format('YYYY-MM-DD HH:mm') : null,
                  detail.approvedAt ? dayjs(detail.approvedAt).format('YYYY-MM-DD HH:mm') : null,
                ]
                  .filter(Boolean)
                  .join(' → ') || '-'}
              </Descriptions.Item>
              {detail.budgetExecution ? (
                <Descriptions.Item label="预算执行">
                  <Money cents={detail.budgetExecution.usedCents} /> 已用 / <Money cents={detail.budgetExecution.pendingCents} /> 在途
                  ，额度 <Money cents={detail.budgetExecution.currentCents} />
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            <div>
              <Typography.Text strong>审批留痕</Typography.Text>
              {(detail.approvals ?? []).length > 0 ? (
                <Timeline
                  style={{ marginTop: 12 }}
                  items={(detail.approvals ?? []).map((a) => ({
                    color: a.status === 'APPROVED' ? 'green' : a.status === 'REJECTED' ? 'red' : 'blue',
                    children: (
                      <div>
                        <Space size={4} wrap>
                          <strong>{a.nodeName}</strong>
                          <Tag>{a.assigneeRoleName ?? '—'}</Tag>
                          {a.assigneeUserName ? <span>{a.assigneeUserName}</span> : null}
                          {a.status === 'PENDING' ? <Tag color="processing">待处理</Tag> : null}
                        </Space>
                        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                          {dayjs(a.handledAt ?? a.createdAt).format('YYYY-MM-DD HH:mm')}
                        </div>
                        {a.comment ? <div style={{ marginTop: 4 }}>{a.comment}</div> : null}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={detail.status === ExpenseApplyStatus.DRAFT ? '尚未提交审批' : '没有审批记录'}
                />
              )}
            </div>
          </Space>
        ) : (
          <Typography.Text type="secondary">{detailLoading ? '加载中…' : ''}</Typography.Text>
        )}
      </Drawer>
    </div>
  );
}

export default ExpenseApplyPage;
